import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { parseLogEvent, type CardKind, type Phase, type Role, type Winner } from './game/types';

export type Lang = 'en' | 'uk';

// ---------------------------------------------------------------------------
// English is the source of truth; the Ukrainian object must match its shape.
// ---------------------------------------------------------------------------

const en = {
  langName: { en: 'English', uk: 'Українська' },

  home: {
    protocol: 'encrypted uplink // v1.0.0',
    contact: 'Contact us — support@neon-fall.com',
    tagline: 'A hidden-role heist for 4–8 runners.',
    features: [
      'Bluff, deduce, and outdraw your rivals.',
      '21 characters, each with a signature power.',
      'Play instantly in any browser — no install.',
    ],
    create: 'Create Game',
    quickPlay: 'Play Now',
    quickPlayHint: 'Instant 5-runner match vs bots',
    join: 'Join by Code',
    publicGames: 'Public Games',
    howto: 'How to Play',
    codex: 'Card Codex',
    settings: 'Settings',
    secure: 'uplink secure',
    newGame: 'New Game',
    turnTimer: 'Turn timer',
    sec: 's',
    min: 'm',
    botDiff: 'Bot difficulty',
    diff: { easy: 'Easy', normal: 'Normal', hard: 'Hard' } as Record<string, string>,
    leaderboard: 'Top Players',
    lbGames: 'games',
    lbMvp: 'MVP',
    lbEmpty: 'No ranked games yet — play one!',
    lbAllTime: 'All-time',
    lbWeek: 'This week',
    profile: 'Profile',
    shop: 'Shop',
    daily: {
      title: 'Daily bonus',
      hint: 'Free coins every day — day 7 pays 100',
      nextIn: (time: string) => `Next bonus in ${time}`,
      day: (n: number) => `Day ${n}`,
      claim: 'Collect',
    },
    seoHeading1: 'Play a Cyberpunk Social Deduction Card Game Online',
    seoBody1:
      'NEONFALL is a hidden-role card game for a group: every player gets a secret objective, and only one faction can win. Read the table, manage your hand, and decide who to trust before it is too late.',
    seoHeading2: 'Secret Roles, Bluffing, Alliances, and Betrayal',
    seoBody2:
      'Each match runs on hidden information. Your role shapes your goal, but every card you play reveals a little more. Bluff, mislead, defend, attack, and survive long enough to expose the real threat.',
    seoHeading3: 'Fast Online Matches for 4–8 Players',
    seoBody3:
      'Create a private room, share the invite code, and start playing directly in the browser — no download, no account required. NEONFALL is built for quick sessions, tense decisions, and chaotic table talk.',
    faqHeading: 'FAQ',
    faq: [
      {
        q: 'What is NEONFALL?',
        a: 'NEONFALL is an online cyberpunk hidden-role card game about bluffing, survival, tactical cards, alliances, and betrayal.',
      },
      {
        q: 'Is NEONFALL a social deduction game?',
        a: 'Yes. NEONFALL uses secret roles and hidden objectives, so players must read each other, bluff, and discover who is really on their side.',
      },
      {
        q: 'Can I play NEONFALL with friends?',
        a: 'Yes — create a private room and share the 6-character code. Friends can join instantly from their browser, no install required.',
      },
      {
        q: 'How many players does NEONFALL support?',
        a: 'NEONFALL is designed for multiplayer matches of 4 to 8 players.',
      },
      {
        q: 'Is NEONFALL like classic hidden-role card games?',
        a: 'NEONFALL is inspired by classic social deduction and hidden-role tabletop games, but uses its own cyberpunk setting, roles, cards, and survival-focused game flow.',
      },
    ],
  },
  coach: {
    gotIt: 'Got it',
    goal: {
      director: 'You are the Director — everyone can see you. Eliminate every Rebel and the Fixer to win.',
      security: 'You secretly guard the Director. Nobody knows your side — keep the boss alive.',
      rebel: 'Your target is the Director. The other Rebels are hidden — watch who attacks whom.',
      fixer: 'Lone wolf: you win only by being the last runner alive.',
    } as Record<Role, string>,
    play: 'Your move: tap a card to play it, or end the turn. Spikes only reach nearby seats.',
    discard: 'Hand limit = your integrity. Discard down to it to end the turn.',
  },
  profile: {
    title: 'Runner Profile',
    games: 'Games',
    wins: 'Wins',
    winrate: 'Win rate',
    mvp: 'MVP',
    byRole: 'By role',
    recent: 'Recent matches',
    empty: 'No matches yet — hit Play Now and make history.',
    won: 'WIN',
    lost: 'LOSS',
    players: 'players',
    streak: (n: number) => `🔥 ${n}-day streak`,
    bestStreak: (n: number) => `best ${n}`,
    coins: 'Coins',
  },
  auth: {
    prompt: 'Sign in to jack in',
    apple: 'Sign in with Apple',
    google: 'Sign in with Google',
    linking: 'linking uplink…',
    keep: 'Your identity keeps your games across devices.',
    soon: 'coming soon',
  },
  join: {
    title: 'Join Game',
    subtitle: 'Enter the 6-character access code.',
    placeholder: 'CODE',
    cancel: 'Cancel',
    action: 'Jack In',
  },
  browse: {
    title: 'Public Games',
    subtitle: 'Join a live lobby looking for runners.',
    empty: 'No public games right now — create one!',
    join: 'Join',
    full: 'Full',
    runners: 'runners',
    refresh: 'Refresh',
    watch: 'Watch',
    live: 'LIVE',
    lobbies: 'Open lobbies',
    liveGames: 'Live now',
  },
  lobby: {
    title: 'Lobby',
    leave: 'Leave',
    accessCode: 'Access Code',
    copy: 'Copy',
    copied: 'Copied',
    invite: 'Invite',
    share: 'Share this code with your crew.',
    runners: 'Runners',
    scanning: 'Scanning for runners…',
    needMore: 'Need at least 4 runners to start.',
    start: 'Start Run',
    waitHost: 'Waiting for the host to start…',
    host: 'Host',
    you: 'You',
    addBot: 'Add Bot',
    bot: 'BOT',
    setup: 'Match Setup',
    setupLocked: 'Match Setup · host only',
    visibility: 'Visibility',
    private: 'Private',
    public: 'Public',
    publicHint: 'Listed in Public Games — anyone can find and join this lobby.',
  },
  reveal: {
    title: 'Incoming Transmission',
    tap: 'Tap to decrypt your assignment.',
    your: 'Your Role',
    runAs: 'You run as',
    power: 'Superpower',
    enter: 'Enter the Grid',
    decrypting: 'decrypting assignment…',
  },
  table: {
    exit: 'Exit',
    cards: 'Cards',
    deck: 'Deck',
    discard: 'Discard',
    discardPrompt: 'Discard down to your integrity',
    noCards: 'no cards',
    waiting: 'waiting for the grid…',
    draw: 'Draw',
    confirm: 'Confirm',
    endTurn: 'End Turn',
    holdToEnd: 'hold to end turn…',
    endTurnConfirmTitle: 'End your turn?',
    endTurnConfirmBody: "You haven't played a card this turn.",
    endTurnKeep: 'Keep playing',
    selectTarget: 'Select a target',
    cancelSelect: 'Cancel',
    deploy: 'Deploy',
    syncing: 'syncing grid…',
    turnOf: (name: string) => `${name}'s turn`,
    yourTurnTitle: 'YOUR TURN',
    factions: 'Factions',
    say: 'Say something…',
    send: 'Send',
    comms: 'Comms',
    robPrompt: (name: string) => `Take from ${name}:`,
    robHand: 'Random from hand',
    giftPrompt: (name: string) => `Send one card to ${name}:`,
    rebootPrompt: 'Choose up to 3 cards to recycle',
    rebootConfirm: (n: number) => `Reboot (${n})`,
    flatTitle: 'You flatlined',
    flatBody: 'Your role is revealed — stick around to see how it ends, or jack into a fresh run.',
    flatWatch: 'Keep watching',
    flatNew: 'New run vs bots',
    surge: 'GRID SURGE',
    ceremonyFlat: 'FLATLINED',
    ceremonyTrace: 'ROLE EXPOSED',
    reasonDraw: 'Draw your cards first',
    reasonNotYourTurn: 'Not your turn yet',
    spectating: 'Spectating',
    spectatingHint: 'Watching live — roles and hands stay hidden until revealed',
  },
  respond: {
    title: 'Incoming Spike!',
    effectTitle: (name: string) => `Incoming ${name}!`,
    takeHit: 'Take Hit',
    letThrough: 'Let it through',
    firewall: 'Firewall',
    airGap: 'Air Gap',
  },
  player: {
    close: 'close',
    online: 'ONLINE',
    flatlined: 'FLATLINED',
    integrity: 'Integrity',
    role: 'Role',
    roleUnknown: 'Unknown — not yet revealed.',
    unknownTag: 'UNKNOWN',
    out: 'OUT',
    character: 'Character',
    gear: 'Gear',
    you: 'You',
  },
  rolePanel: { your: 'Your Role', character: 'Character', back: 'Back to the grid' },
  codex: { title: 'Card Codex', close: 'close', inDeck: 'in deck', total: (n: number) => `${n} cards in the deck` },
  gameover: {
    title: 'Run Complete',
    roster: 'Final roster',
    rematch: 'Rematch',
    waitHost: 'Waiting for the host to restart the run…',
    exit: 'Back to Base',
    share: 'Share Result',
    shareCopied: 'Copied to clipboard!',
    shareWin: 'I won as {role} in NEONFALL — a cyberpunk hidden-role card shootout. Think you can read the table?',
    shareLoss: 'I got flatlined as {role} in NEONFALL — a cyberpunk hidden-role card shootout. Avenge me?',
    shareMvp: '★ MVP of the match.',
    coins: (n: number) => `+${n} coins earned`,
  },
  shop: {
    title: 'Neon Shop',
    subtitle: 'Spend coins earned in matches',
    balance: 'Balance',
    earnHint: (g: number, w: number, m: number) =>
      `Earn ${g} per match · +${w} win bonus · +${m} MVP bonus`,
    frames: 'Avatar frames',
    cardbacks: 'Card backs',
    buy: 'Buy',
    owned: 'Owned',
    equip: 'Equip',
    equipped: 'Equipped',
    stock: 'Standard',
    notEnough: 'Not enough coins — play a few matches!',
    frameNames: {
      cyan: 'Cyan Circuit',
      magenta: 'Magenta Pulse',
      gold: 'Gold Standard',
    } as Record<string, string>,
    cardbackNames: {
      cyan: 'Cyan Circuit',
      magenta: 'Synthwave',
      violet: 'Void Violet',
      gold: 'Gilded Deck',
    } as Record<string, string>,
  },
  settings: {
    title: 'Settings',
    close: 'close',
    handle: 'Handle',
    nameColor: 'Nick colour',
    locked: 'Locked',
    account: 'Account',
    guest: 'Playing as guest',
    signOut: 'Sign out',
    save: 'Save',
    language: 'Language',
    sound: 'Sound effects',
    music: 'Music',
    haptics: 'Vibration',
    notify: 'Notify me (my turn, match ready)',
    notifyBusy: 'Enabling notifications…',
    signedInWith: (p: string) => `Signed in with ${p}`,
    about: 'NEONFALL — web build. Game logic runs server-side.',
  },
  flash: {
    shield: { title: 'SHIELD HELD', sub: 'Your cipher-shield absorbed the Spike' },
    firewall: { title: 'FIREWALL UP', sub: 'You cancelled the Spike' },
    free: { title: 'BROKE FREE', sub: 'You shook off the downtime' },
    reboot: { title: 'REBOOTED', sub: 'Back from the brink' },
    locked: { title: 'LOCKED DOWN', sub: 'Downtime — your turn is skipped' },
    bounty: { title: 'BOUNTY CLAIMED', sub: 'Rebel down — draw 3 cards' },
    penalty: { title: 'FRIENDLY FIRE', sub: 'You downed your Security — all cards lost' },
  },
  categories: {
    attack: 'Attack',
    defense: 'Defense',
    gear: 'Gear & Weapons',
    sabotage: 'Sabotage',
    economy: 'Resources',
    recon: 'Recon',
  } as Record<string, string>,
  phase: { draw: 'Draw', play: 'Play', discard: 'Discard', finished: 'Finished' } as Record<Phase, string>,
  winner: {
    directorSide: 'Corp Wins',
    rebels: 'Rebels Win',
    fixer: 'The Fixer Wins',
  } as Record<Winner, string>,
  role: {
    name: {
      director: 'Director',
      security: 'Security',
      rebel: 'Rebel',
      fixer: 'Fixer',
    } as Record<Role, string>,
    goal: {
      director: 'Survive. Purge every Rebel and Fixer from the grid.',
      security: 'Protect the Director at any cost. Stay hidden.',
      rebel: 'Bring the Director down. Trust no one.',
      fixer: 'Outlast them all. Be the last one standing.',
    } as Record<Role, string>,
    desc: {
      director:
        'The corp boss — revealed from the start with +1 integrity. Everyone sees who you are, but not who is loyal. Wipe out every Rebel and Fixer to win.',
      security:
        'Loyal muscle hidden in the crowd. You win alongside the Director, and nobody knows you are on their side — use that cover to keep the boss breathing.',
      rebel:
        'You want the Director dead — and so do the other Rebels, but you do not know who they are. Read the table and coordinate through your plays, not your words.',
      fixer:
        'A lone wildcard. You do not care who wins the war — you win only by being the very last runner left alive on the grid.',
    } as Record<Role, string>,
  },
  card: {
    name: {
      hack: 'Spike',
      firewall: 'Firewall',
      cipherShield: 'Cipher Shield',
      rig: 'Rig',
      overclock: 'Overclock',
      longwave: 'Longwave',
      raid: 'Raid',
      downtime: 'Downtime',
      medPatch: 'Med-Patch',
      payday: 'Payday',
      duel: 'Duel',
      emp: 'EMP Burst',
      virus: 'Virus',
      siphon: 'Siphon',
      stim: 'Adrenaline',
      trace: 'Trace',
      airGap: 'Air Gap',
      killSwitch: 'Kill Switch',
      backchannel: 'Backchannel',
      fieldPatch: 'Field Patch',
      coldReboot: 'Cold Reboot',
      packetStorm: 'Packet Storm',
    } as Record<CardKind, string>,
    desc: {
      hack: '1 damage to a runner in reach. One per turn.',
      firewall: 'Cancels a Spike aimed at you.',
      cipherShield: 'Equip: chance to auto-block Spikes.',
      rig: 'Weapon: +1 reach.',
      overclock: 'Weapon: unlimited Spikes per turn.',
      longwave: 'Weapon: +2 reach.',
      raid: 'A target loses a card — pick a piece of their gear, or a random one from hand.',
      downtime: 'Plant: target may lose their next turn.',
      medPatch: 'Restore 1 integrity.',
      payday: 'Draw 2 cards.',
      duel: 'Spike-off: first side out of Spikes takes 1.',
      emp: 'Every other player is fried down to a single card.',
      virus: 'Plant: 1 damage per turn; may spread or burn out.',
      siphon: 'Take a card into your hand — pick the target’s gear, or a random one from hand.',
      stim: 'Restore 2 integrity.',
      trace: 'Expose a target’s hidden role to everyone.',
      airGap: 'Reaction: cancel a Duel, Raid, Siphon, Trace, Virus, or Downtime aimed at you.',
      killSwitch: 'Discard one face-up piece of gear anywhere on the grid, including your own.',
      backchannel: 'Give another runner one card face-down, then draw 2 cards.',
      fieldPatch: 'Restore 1 integrity to another runner. Disabled in a one-on-one.',
      coldReboot: 'Recycle up to 3 other cards and draw one more than you recycled.',
      packetStorm: 'Every living runner passes one random card clockwise at the same time.',
    } as Record<CardKind, string>,
    short: {
      hack: '1 damage',
      firewall: 'Cancel Spike',
      cipherShield: 'Block chance',
      rig: '+1 reach',
      overclock: 'No Spike limit',
      longwave: '+2 reach',
      raid: 'Steal / burn gear',
      downtime: 'Skip next turn',
      medPatch: 'Heal 1',
      payday: 'Draw 2',
      duel: 'Spike-off',
      emp: 'Mass discard',
      virus: 'Damage over time',
      siphon: 'Steal a card',
      stim: 'Heal 2',
      trace: 'Reveal role',
      airGap: 'Cancel non-Spike',
      killSwitch: 'Destroy gear',
      backchannel: 'Give 1 · draw 2',
      fieldPatch: 'Heal another 1',
      coldReboot: 'Recycle up to 3',
      packetStorm: 'Chaos swap',
    } as Record<CardKind, string>,
  },
  ability: {
    drawOnHack: 'Draws a bonus card with every Spike.',
    bonusIntegrity: 'Starts with +1 integrity.',
    revive2: 'Reboots once at 2 integrity instead of flatlining.',
    multiHack: 'Once per run: a second Spike in one turn.',
    drawFromDiscard: 'Salvages the discard top when drawing.',
    drawOnDamage: 'Draws a card each time they take damage.',
    innateShield: 'Built-in chance to auto-block a Spike.',
    noHackLimit: 'No one-Spike-per-turn limit.',
    extraReach: '+1 attack reach, always.',
    evasive: 'Others need +1 reach to hit them.',
    drawWhenEmpty: 'Draws a card the moment their hand empties.',
    pierceBlock: 'Their Spikes burn through auto-block shields.',
    bonusHeal: 'Med-patches heal them 2 instead of 1.',
    extraDraw: 'Draws 3 cards instead of 2.',
    stealOnDamage: 'Drains a card from whoever damages them.',
    regenChance: '50% chance to self-repair 1 at turn start.',
    gearImmune: 'Downtime and Virus can never stick to them.',
    handLimitBonus: 'Keeps 2 cards over the hand limit.',
    lucky: 'Luck rolls (blocks, downtime) roll twice, favourably.',
    retaliate: 'Goes down swinging — their killer takes 1 damage.',
    none: 'No ability — just thick plating.',
  } as Record<string, string>,
  log: {
    start: (d: string) => `run start — ${d} is the Director`,
    draw: (p: string) => `${p} draws`,
    salvage: (p: string, k: string) => `${p} salvages ${k} from the discard`,
    spike: (a: string, b: string) => `${a} spikes ${b}`,
    shield: (p: string) => `${p}'s cipher-shield absorbs the Spike`,
    mayFw: (p: string) => `${p} may raise a Firewall`,
    fw: (p: string) => `${p} cancels the Spike with a Firewall`,
    burst: (p: string) => `${p} burns their ability for an extra Spike`,
    ride: (p: string) => `${p} rides the Spike for a bonus draw`,
    duel: (a: string, b: string, l: string) => `${a} duels ${b} — ${l} loses`,
    raid: (a: string, b: string) => `${a} raids ${b}'s cache`,
    siphon: (a: string, b: string) => `${a} siphons a card from ${b}`,
    stim: (p: string) => `${p} slams an adrenaline shot`,
    trace: (a: string, b: string, r: string) => `${a} traces ${b} — ${r}`,
    airGap: (p: string, k: string) => `${p} air-gaps ${k}`,
    killSwitch: (a: string, b: string, k: string) => `${a} kills ${b}'s ${k}`,
    backchannel: (a: string, b: string) => `${a} opens a backchannel to ${b}`,
    fieldPatch: (a: string, b: string) => `${a} patches ${b}`,
    coldReboot: (p: string, n: number) => `${p} cold-reboots ${n} cards`,
    packetStorm: (p: string) => `${p} unleashes a packet storm`,
    emp: (p: string) => `${p} floods the grid with an EMP burst`,
    med: (p: string) => `${p} applies a med-patch`,
    pay: (p: string) => `${p} cashes a payday`,
    equip: (p: string, k: string) => `${p} equips ${k}`,
    plant: (p: string, k: string, b: string) => `${p} plants ${k} on ${b}`,
    mustDiscard: (p: string, n: number) => `${p} must discard down to ${n}`,
    discard: (p: string, n: number) => `${p} discards ${n}`,
    dmg: (p: string, n: number) => `${p} takes ${n} damage`,
    surge: (p: string, n: number) => `grid surge — ${p} takes ${n} (sudden death)`,
    reboot: (p: string) => `${p} reboots from the brink`,
    leech: (a: string, b: string) => `${a} drains a card from ${b}`,
    regen: (p: string) => `${p} self-repairs +1`,
    lash: (a: string, b: string) => `${a} goes down swinging — ${b} takes 1`,
    bounty: (p: string) => `${p} claims the bounty — draws 3 cards`,
    penalty: (p: string) => `${p} downed their own Security — dumps every card`,
    flat: (p: string, r: string) => `${p} flatlines — was ${r}`,
    leave: (p: string) => `${p} disconnects from the grid`,
    virus: (p: string) => `the virus gnaws at ${p}`,
    virusJump: (p: string) => `the virus jumps to ${p}`,
    virusOut: () => 'the virus burns out',
    dtSkip: (p: string) => `${p} is locked in downtime — turn skipped`,
    dtFree: (p: string) => `${p} shakes off the downtime`,
    reshuffle: () => 'the grid reshuffles the discard into a fresh deck',
    toHit: (p: string) => `${p} freezes — the Spike lands (timeout)`,
    toSkip: (p: string) => `${p} stalls — turn skipped (timeout)`,
    win: (w: string) => `run complete: ${w}`,
  },
  stats: {
    title: 'Match stats',
    mvp: 'MVP',
    dealt: 'DMG',
    taken: 'Taken',
    spikes: 'Spikes',
    blocks: 'Blocks',
    heals: 'Heals',
  },
  howto: {
    title: 'How to Play',
    intro:
      'NEONFALL is a hidden-role shootout on a neon grid. 4–8 runners, secret factions, one public Director. Read the room, spend your cards well, and be the last faction standing.',
    factionsHeader: 'The Factions',
    charactersHeader: 'Characters',
    charactersNote: 'Each runner is dealt a random character — ♥ is their starting integrity (the Director gets +1 on top):',
    cardsHeader: 'The Cards',
    cardsNote: 'Tap any runner in-game, or open the Card Codex, for every card by category.',
    sections: [
      { h: 'The Goal', b: '4–8 runners sit in a ring, split into secret factions. The Director is public; everyone else is a mystery. When only one faction is left alive, it wins.' },
      { h: 'Your Turn', b: 'Three beats: DRAW two cards, PLAY cards (only one Spike per turn unless your gear says otherwise), then DISCARD down to your integrity.' },
      { h: 'Network Proximity', b: 'Range is everything. You can only Spike runners within reach — base reach is 1 seat. Weapons extend it; shields keep others out of yours. As runners flatline, the ring tightens and far targets come into range.' },
      { h: 'Combat', b: 'A Spike deals 1 damage. The target can answer with a Firewall to cancel it, or a Cipher Shield may auto-block by chance. No answer, no integrity — hit zero and you flatline, revealing your role.' },
      { h: 'Bounties', b: 'Take down a Rebel and you draw 3 cards as bounty. But a Director who flatlines their own Security is shamed — they dump their entire hand and gear to the discard.' },
      { h: 'Winning', b: 'Corp (Director + Security) wins by purging every Rebel and Fixer. Rebels win by killing the Director. The lone Fixer wins only by being the last runner alive.' },
    ],
  },
  // Bot trash-talk, keyed so each client renders it in its own language. The
  // Director barks jargon-y orders; everyone else bluffs innocence. Slangy on
  // purpose — it should read like grid-runner chatter, not menu copy.
  botLines: {
    dir_fixer: 'Fixer first.',
    dir_focus: 'Focus fire.',
    dir_guilty: 'You look guilty.',
    dir_guard: 'Wall me up.',
    dir_liars: 'Two of you lie.',
    dir_rebels: 'Rebels are close.',
    bluff_loyal: "I'm loyal.",
    bluff_withDir: 'I ride Corp.',
    bluff_clean: "I'm clean af, trust 😇",
    bluff_noSpike: 'Save that Spike.',
    bluff_quiet: 'Watch the quiet ones.',
    bluff_notMe: "Wasn't me.",
    bluff_wrong: 'Wrong target.',
    bluff_gotBack: "Director's back.",
    bluff_someone: 'Someone is lying.',
    bluff_cover: 'Cover me.',
  } as Record<string, string>,
};

export type Copy = typeof en;

const uk: Copy = {
  langName: { en: 'English', uk: 'Українська' },

  home: {
    protocol: 'зашифрований аплінк // v1.0.0',
    contact: "Зв'язатися — support@neon-fall.com",
    tagline: 'Гра з прихованими ролями для 4–8 ранерів.',
    features: [
      'Блефуй, вираховуй і переграй суперників.',
      '21 персонаж, у кожного — унікальна сила.',
      'Грай миттєво в будь-якому браузері — без установки.',
    ],
    create: 'Створити гру',
    quickPlay: 'Грати зараз',
    quickPlayHint: 'Миттєвий матч на 5 ранерів з ботами',
    join: 'Увійти за кодом',
    publicGames: 'Публічні ігри',
    howto: 'Як грати',
    codex: 'Кодекс карт',
    settings: 'Налаштування',
    secure: 'аплінк захищено',
    newGame: 'Нова гра',
    turnTimer: 'Таймер ходу',
    sec: 'с',
    min: 'хв',
    botDiff: 'Складність ботів',
    diff: { easy: 'Легкі', normal: 'Звичайні', hard: 'Складні' },
    leaderboard: 'Топ гравців',
    lbGames: 'ігор',
    lbMvp: 'MVP',
    lbEmpty: 'Ще немає рейтингових ігор — зіграйте першу!',
    lbAllTime: 'За весь час',
    lbWeek: 'Цей тиждень',
    profile: 'Профіль',
    shop: 'Магазин',
    daily: {
      title: 'Щоденний бонус',
      hint: 'Безкоштовні коіни щодня — 7-й день дає 100',
      nextIn: (time: string) => `Наступний бонус через ${time}`,
      day: (n: number) => `День ${n}`,
      claim: 'Зібрати',
    },
    seoHeading1: 'Онлайн-гра з блефом, таємними ролями та виживанням',
    seoBody1:
      'NEONFALL — це карткова гра з прихованими ролями для компанії: кожен гравець отримує таємну мету, і перемогти може лише одна фракція. Читай стіл, керуй рукою і вирішуй, кому довіряти, поки не пізно.',
    seoHeading2: 'Таємні ролі, блеф, союзи й зрада',
    seoBody2:
      'Кожна партія побудована на прихованій інформації. Твоя роль визначає мету, але кожна зіграна карта видає трохи більше. Блефуй, вводь в оману, захищайся, атакуй і виживи достатньо довго, щоб викрити справжню загрозу.',
    seoHeading3: 'Швидкі онлайн-партії для 4–8 гравців',
    seoBody3:
      'Створи приватну кімнату, поділись кодом запрошення і грай прямо в браузері — без завантажень і без обов’язкової реєстрації. NEONFALL створений для швидких сесій, напружених рішень і хаотичних розмов за столом.',
    faqHeading: 'Часті запитання',
    faq: [
      {
        q: 'Що таке NEONFALL?',
        a: 'NEONFALL — це онлайн карткова гра у кіберпанк-світі про блеф, виживання, тактичні карти, союзи та зраду.',
      },
      {
        q: 'Чи NEONFALL — це гра з соціальною дедукцією?',
        a: 'Так. NEONFALL використовує таємні ролі та приховані цілі, тож гравці мають читати одне одного, блефувати й з’ясовувати, хто насправді на їхньому боці.',
      },
      {
        q: 'Чи можна грати в NEONFALL з друзями?',
        a: 'Так — створи приватну кімнату та поділись 6-символьним кодом. Друзі можуть приєднатися миттєво з браузера, без встановлення.',
      },
      {
        q: 'Скільки гравців підтримує NEONFALL?',
        a: 'NEONFALL розрахований на мультиплеєрні партії від 4 до 8 гравців.',
      },
      {
        q: 'Чи схожий NEONFALL на класичні ігри з таємними ролями?',
        a: 'NEONFALL надихнувся класичними настільними іграми з соціальною дедукцією та таємними ролями, але має власний кіберпанк-сетинг, ролі, карти й фокус на виживанні.',
      },
    ],
  },
  coach: {
    gotIt: 'Зрозуміло',
    goal: {
      director: 'Ти Директор — тебе бачать усі. Переможи, прибравши всіх Бунтарів і Фіксера.',
      security: 'Ти таємно охороняєш Директора. Твою сторону не знає ніхто — тримай боса живим.',
      rebel: 'Твоя ціль — Директор. Інші Бунтарі приховані: стеж, хто кого атакує.',
      fixer: 'Вовк-одинак: перемагаєш, лише якщо виживеш останнім.',
    },
    play: 'Твій хід: тапни карту, щоб зіграти, або заверши хід. Спайк дістає лише сусідні місця.',
    discard: 'Ліміт руки = твоя цілісність. Скинь зайві карти, щоб завершити хід.',
  },
  profile: {
    title: 'Профіль ранера',
    games: 'Ігор',
    wins: 'Перемог',
    winrate: 'Вінрейт',
    mvp: 'MVP',
    byRole: 'За ролями',
    recent: 'Останні матчі',
    empty: 'Ще немає матчів — тисни «Грати зараз» і твори історію.',
    won: 'WIN',
    lost: 'LOSS',
    players: 'гравців',
    streak: (n: number) => `🔥 Серія: ${n} дн. поспіль`,
    bestStreak: (n: number) => `рекорд ${n}`,
    coins: 'Коіни',
  },
  auth: {
    prompt: 'Увійдіть, щоб підключитися',
    apple: 'Увійти з Apple',
    google: 'Увійти з Google',
    linking: "з'єднання з аплінком…",
    keep: 'Ваш профіль зберігає ігри між пристроями.',
    soon: 'скоро',
  },
  join: {
    title: 'Увійти в гру',
    subtitle: 'Введіть 6-значний код доступу.',
    placeholder: 'КОД',
    cancel: 'Скасувати',
    action: 'Увійти',
  },
  browse: {
    title: 'Публічні ігри',
    subtitle: 'Приєднуйся до живого лобі, що шукає ранерів.',
    empty: 'Зараз немає публічних ігор — створи свою!',
    join: 'Увійти',
    full: 'Повне',
    runners: 'ранерів',
    refresh: 'Оновити',
    watch: 'Дивитися',
    live: 'НАЖИВО',
    lobbies: 'Відкриті лобі',
    liveGames: 'Зараз грають',
  },
  lobby: {
    title: 'Лобі',
    leave: 'Вийти',
    accessCode: 'Код доступу',
    copy: 'Копіювати',
    copied: 'Скопійовано',
    invite: 'Запросити',
    share: 'Поділіться кодом зі своєю командою.',
    runners: 'Ранери',
    scanning: 'Скануємо мережу…',
    needMore: 'Потрібно щонайменше 4 ранери для старту.',
    start: 'Почати забіг',
    waitHost: 'Чекаємо, поки хост розпочне…',
    host: 'Хост',
    you: 'Ви',
    addBot: 'Додати бота',
    bot: 'БОТ',
    setup: 'Налаштування матчу',
    setupLocked: 'Налаштування матчу · лише хост',
    visibility: 'Видимість',
    private: 'Приватна',
    public: 'Публічна',
    publicHint: 'У списку публічних ігор — будь-хто може знайти й приєднатися.',
  },
  reveal: {
    title: 'Вхідна передача',
    tap: 'Торкніться, щоб розшифрувати призначення.',
    your: 'Ваша роль',
    runAs: 'Ви граєте за',
    power: 'Надздібність',
    enter: 'Увійти в мережу',
    decrypting: 'розшифровуємо призначення…',
  },
  table: {
    exit: 'Вийти',
    cards: 'Карти',
    deck: 'Колода',
    discard: 'Скид',
    discardPrompt: 'Скиньте карти до рівня цілісності',
    noCards: 'немає карт',
    waiting: 'очікуємо мережу…',
    draw: 'Добрати',
    confirm: 'Підтвердити',
    endTurn: 'Кінець ходу',
    holdToEnd: 'утримуйте, щоб завершити…',
    endTurnConfirmTitle: 'Закінчити хід?',
    endTurnConfirmBody: 'Ви не зіграли жодної картки цього ходу.',
    endTurnKeep: 'Ще граю',
    selectTarget: 'Оберіть ціль',
    cancelSelect: 'Скасувати',
    deploy: 'Застосувати',
    syncing: 'синхронізація…',
    turnOf: (name: string) => `хід: ${name}`,
    yourTurnTitle: 'ТВІЙ ХІД',
    factions: 'Фракції',
    say: 'Напишіть щось…',
    send: 'Надіслати',
    comms: 'Зв’язок',
    robPrompt: (name: string) => `Забрати в ${name}:`,
    robHand: 'Випадкова з руки',
    giftPrompt: (name: string) => `Передати одну карту гравцю ${name}:`,
    rebootPrompt: 'Оберіть до 3 карт для перезапуску',
    rebootConfirm: (n: number) => `Перезапуск (${n})`,
    flatTitle: 'Тебе відключили',
    flatBody: 'Твою роль розкрито — залишся подивитися, чим усе скінчиться, або залітай у новий забіг.',
    flatWatch: 'Дивитися далі',
    flatNew: 'Новий забіг з ботами',
    surge: 'ПЕРЕВАНТАЖЕННЯ',
    ceremonyFlat: 'ВІДКЛЮЧЕНО',
    ceremonyTrace: 'РОЛЬ РОЗКРИТО',
    reasonDraw: 'Спершу добери карти',
    reasonNotYourTurn: 'Зараз не твій хід',
    spectating: 'Спостереження',
    spectatingHint: 'Дивишся наживо — ролі та руки приховані, поки не розкриються',
  },
  respond: {
    title: 'Вхідний Спайк!',
    effectTitle: (name: string) => `Вхідна карта: ${name}!`,
    takeHit: 'Пропустити',
    letThrough: 'Пропустити ефект',
    firewall: 'Фаєрвол',
    airGap: 'Ейрґеп',
  },
  player: {
    close: 'закрити',
    online: 'В МЕРЕЖІ',
    flatlined: 'ВИБУВ',
    integrity: 'Цілісність',
    role: 'Роль',
    roleUnknown: 'Невідома — ще не розкрита.',
    unknownTag: 'НЕВІДОМО',
    out: 'ВИБУВ',
    character: 'Персонаж',
    gear: 'Спорядження',
    you: 'Ви',
  },
  rolePanel: { your: 'Ваша роль', character: 'Персонаж', back: 'Назад у мережу' },
  codex: { title: 'Кодекс карт', close: 'закрити', inDeck: 'у колоді', total: (n: number) => `${n} карт у колоді` },
  gameover: {
    title: 'Забіг завершено',
    roster: 'Фінальний склад',
    rematch: 'Реванш',
    waitHost: 'Чекаємо, поки хост перезапустить забіг…',
    exit: 'На базу',
    share: 'Поділитися результатом',
    shareCopied: 'Скопійовано!',
    shareWin: 'Я переміг за {role} у NEONFALL — кіберпанк-грі з таємними ролями. Зможеш прочитати стіл?',
    shareLoss: 'Мене відключили за {role} у NEONFALL — кіберпанк-грі з таємними ролями. Помстишся за мене?',
    shareMvp: '★ MVP матчу.',
    coins: (n: number) => `+${n} коінів зароблено`,
  },
  shop: {
    title: 'Неоновий магазин',
    subtitle: 'Витрачайте коіни, зароблені в матчах',
    balance: 'Баланс',
    earnHint: (g: number, w: number, m: number) =>
      `Заробляйте ${g} за матч · +${w} за перемогу · +${m} за MVP`,
    frames: 'Рамки аватара',
    cardbacks: 'Сорочки карт',
    buy: 'Купити',
    owned: 'Придбано',
    equip: 'Обрати',
    equipped: 'Обрано',
    stock: 'Стандарт',
    notEnough: 'Недостатньо коінів — зіграйте кілька матчів!',
    frameNames: {
      cyan: 'Блакитний контур',
      magenta: 'Пурпуровий пульс',
      gold: 'Золотий стандарт',
    },
    cardbackNames: {
      cyan: 'Блакитна схема',
      magenta: 'Синтвейв',
      violet: 'Фіолетова порожнеча',
      gold: 'Позолочена колода',
    },
  },
  settings: {
    title: 'Налаштування',
    close: 'закрити',
    handle: 'Позивний',
    nameColor: 'Колір ніка',
    locked: 'Заблоковано',
    account: 'Обліковий запис',
    guest: 'Ви граєте як гість',
    signOut: 'Вийти',
    save: 'Зберегти',
    language: 'Мова',
    sound: 'Звукові ефекти',
    music: 'Музика',
    haptics: 'Вібрація',
    notify: 'Сповіщати мене (мій хід, матч готовий)',
    notifyBusy: 'Вмикаємо сповіщення…',
    signedInWith: (p: string) => `Вхід через ${p}`,
    about: 'NEONFALL — веб-збірка. Ігрова логіка виконується на сервері.',
  },
  flash: {
    shield: { title: 'ЩИТ ТРИМАЄ', sub: 'Шифрощит поглинув Спайк' },
    firewall: { title: 'ФАЄРВОЛ', sub: 'Ви скасували Спайк' },
    free: { title: 'НА ВОЛІ', sub: 'Ви скинули простій' },
    reboot: { title: 'ПЕРЕЗАВАНТАЖЕННЯ', sub: 'Повернення з межі' },
    locked: { title: 'ЗАБЛОКОВАНО', sub: 'Простій — ваш хід пропущено' },
    bounty: { title: 'НАГОРОДА', sub: 'Повстанця усунуто — тягніть 3 карти' },
    penalty: { title: 'ДРУЖНІЙ ВОГОНЬ', sub: 'Ви усунули свою Безпеку — всі карти втрачено' },
  },
  categories: {
    attack: 'Атака',
    defense: 'Захист',
    gear: 'Спорядження та зброя',
    sabotage: 'Диверсії',
    economy: 'Ресурси',
    recon: 'Розвідка',
  },
  phase: { draw: 'Добір', play: 'Хід', discard: 'Скид', finished: 'Завершено' },
  winner: {
    directorSide: 'Корпорація перемогла',
    rebels: 'Бунтарі перемогли',
    fixer: 'Фіксер переміг',
  },
  role: {
    name: { director: 'Директор', security: 'Охорона', rebel: 'Бунтар', fixer: 'Фіксер' },
    goal: {
      director: 'Виживіть. Зачистіть мережу від усіх Бунтарів і Фіксерів.',
      security: 'Захищайте Директора за будь-яку ціну. Залишайтеся в тіні.',
      rebel: 'Знищте Директора. Не довіряйте нікому.',
      fixer: 'Переживіть усіх. Станьте останнім у мережі.',
    },
    desc: {
      director:
        'Бос корпорації — відкритий від початку з +1 цілісності. Усі бачать, хто ви, але не знають, хто вам вірний. Щоб перемогти, зачистіть усіх Бунтарів і Фіксерів.',
      security:
        'Вірна сила, схована в натовпі. Ви перемагаєте разом із Директором, і ніхто не знає, що ви на його боці — використовуйте це прикриття, щоб уберегти боса.',
      rebel:
        'Ви хочете смерті Директора — як і інші Бунтарі, але ви не знаєте, хто вони. Читайте стіл і координуйтеся ходами, а не словами.',
      fixer:
        'Самотній джокер. Вам байдуже, хто виграє війну — ви перемагаєте, лише залишившись останнім живим ранером у мережі.',
    },
  },
  card: {
    name: {
      hack: 'Спайк',
      firewall: 'Фаєрвол',
      cipherShield: 'Шифрощит',
      rig: 'Риг',
      overclock: 'Розгін',
      longwave: 'Довга хвиля',
      raid: 'Рейд',
      downtime: 'Простій',
      medPatch: 'Мед-патч',
      payday: 'Виплата',
      duel: 'Дуель',
      emp: 'ЕМІ-вибух',
      virus: 'Вірус',
      siphon: 'Сифон',
      stim: 'Адреналін',
      trace: 'Трейс',
      airGap: 'Ейрґеп',
      killSwitch: 'Рубильник',
      backchannel: 'Бекканал',
      fieldPatch: 'Медевак',
      coldReboot: 'Ребут',
      packetStorm: 'Флуд',
    },
    desc: {
      hack: '1 шкоди ранеру в радіусі. Один за хід.',
      firewall: 'Скасовує Спайк, націлений на вас.',
      cipherShield: 'Спорядження: шанс автоблоку Спайків.',
      rig: 'Зброя: +1 до радіуса.',
      overclock: 'Зброя: Спайки без ліміту за хід.',
      longwave: 'Зброя: +2 до радіуса.',
      raid: 'Ціль втрачає карту — оберіть її спорядження або випадкову з руки.',
      downtime: 'Закладка: ціль може втратити наступний хід.',
      medPatch: 'Відновлює 1 цілісності.',
      payday: 'Доберіть 2 карти.',
      duel: 'Спайк-дуель: хто перший без Спайків — отримує 1 шкоди.',
      emp: 'Усі інші скидають руку до однієї картки.',
      virus: 'Закладка: 1 шкоди щохода; може перекинутися або згаснути.',
      siphon: 'Заберіть карту собі — оберіть спорядження цілі або випадкову з руки.',
      stim: 'Відновлює 2 цілісності.',
      trace: 'Розкриває приховану роль цілі для всіх.',
      airGap: 'Реакція: скасуйте Дуель, Рейд, Сифон, Трейс, Вірус або Простій проти вас.',
      killSwitch: 'Скиньте одне відкрите спорядження будь-де в мережі, зокрема власне.',
      backchannel: 'Передайте іншому ранеру одну карту долілиць, потім доберіть 2.',
      fieldPatch: 'Відновіть 1 цілісності іншому ранеру. Недоступно віч-на-віч.',
      coldReboot: 'Замініть до 3 інших карт і доберіть на одну більше, ніж скинули.',
      packetStorm: 'Усі живі ранери одночасно передають випадкову карту за годинниковою стрілкою.',
    },
    short: {
      hack: '1 шкоди',
      firewall: 'Скасувати Спайк',
      cipherShield: 'Шанс автоблоку',
      rig: '+1 радіус',
      overclock: 'Без ліміту',
      longwave: '+2 радіус',
      raid: 'Вкрасти обвіс',
      downtime: 'Пропуск ходу',
      medPatch: 'Лікує 1',
      payday: 'Добір 2',
      duel: 'Спайк-дуель',
      emp: 'Масовий скид',
      virus: 'Шкода щохода',
      siphon: 'Вкрасти карту',
      stim: 'Лікує 2',
      trace: 'Розкрити роль',
      airGap: 'Скасувати ціль',
      killSwitch: 'Знищити обвіс',
      backchannel: 'Дай 1 · бери 2',
      fieldPatch: 'Хіл іншому 1',
      coldReboot: 'Замінити до 3',
      packetStorm: 'Хаос-обмін',
    },
  },
  ability: {
    drawOnHack: 'Добирає бонусну карту з кожним Спайком.',
    bonusIntegrity: 'Починає з +1 цілісності.',
    revive2: 'Раз за гру перезавантажується з 2 цілісності замість смерті.',
    multiHack: 'Раз за гру: другий Спайк за один хід.',
    drawFromDiscard: 'Підбирає верх скиду під час добору.',
    drawOnDamage: 'Добирає карту щоразу, коли отримує шкоду.',
    innateShield: 'Вбудований шанс автоблоку Спайка.',
    noHackLimit: 'Без ліміту на Спайки за хід.',
    extraReach: '+1 до радіуса атаки завжди.',
    evasive: 'Іншим потрібно +1 радіус, щоб влучити.',
    drawWhenEmpty: 'Добирає карту, щойно рука порожніє.',
    pierceBlock: 'Його Спайки пропалюють автоблок-щити.',
    bonusHeal: 'Мед-патчі лікують йому 2 замість 1.',
    extraDraw: 'Добирає 3 карти замість 2.',
    stealOnDamage: 'Витягує карту в того, хто завдав йому шкоди.',
    regenChance: 'Шанс 50% відновити 1 цілісність на початку ходу.',
    gearImmune: 'Простій і Вірус до нього не чіпляються.',
    handLimitBonus: 'Тримає 2 карти понад ліміт руки.',
    lucky: 'Кидки удачі (блоки, простій) кидаються двічі на його користь.',
    retaliate: 'Гине з боєм — убивця отримує 1 шкоди.',
    none: 'Без здібності — лише товста броня.',
  },
  log: {
    start: (d: string) => `початок забігу — ${d} є Директором`,
    draw: (p: string) => `${p} добирає карти`,
    salvage: (p: string, k: string) => `${p} підбирає ${k} зі скиду`,
    spike: (a: string, b: string) => `${a} спайкає ${b}`,
    shield: (p: string) => `шифрощит ${p} поглинає Спайк`,
    mayFw: (p: string) => `${p} може підняти Фаєрвол`,
    fw: (p: string) => `${p} скасовує Спайк Фаєрволом`,
    burst: (p: string) => `${p} палить здібність заради ще одного Спайка`,
    ride: (p: string) => `${p} добирає бонусну карту зі Спайком`,
    duel: (a: string, b: string, l: string) => `${a} викликає ${b} на дуель — ${l} програє`,
    raid: (a: string, b: string) => `${a} рейдить кеш ${b}`,
    siphon: (a: string, b: string) => `${a} перехоплює карту в ${b}`,
    stim: (p: string) => `${p} вколює адреналін`,
    trace: (a: string, b: string, r: string) => `${a} трасує ${b} — ${r}`,
    airGap: (p: string, k: string) => `${p} ізолюється від карти ${k}`,
    killSwitch: (a: string, b: string, k: string) => `${a} вимикає ${k} у ${b}`,
    backchannel: (a: string, b: string) => `${a} відкриває таємний канал до ${b}`,
    fieldPatch: (a: string, b: string) => `${a} лікує ${b} польовим патчем`,
    coldReboot: (p: string, n: number) => `${p} перезапускає ${n} карт`,
    packetStorm: (p: string) => `${p} запускає пакетний шторм`,
    emp: (p: string) => `${p} накриває мережу ЕМІ-вибухом`,
    med: (p: string) => `${p} застосовує мед-патч`,
    pay: (p: string) => `${p} отримує виплату`,
    equip: (p: string, k: string) => `${p} споряджає: ${k}`,
    plant: (p: string, k: string, b: string) => `${p} підкидає ${k} гравцю ${b}`,
    mustDiscard: (p: string, n: number) => `${p} мусить скинути до ${n}`,
    discard: (p: string, n: number) => `${p} скидає ${n}`,
    dmg: (p: string, n: number) => `${p} отримує ${n} шкоди`,
    surge: (p: string, n: number) => `прорив мережі — ${p} отримує ${n} (раптова смерть)`,
    reboot: (p: string) => `${p} перезавантажується на межі`,
    leech: (a: string, b: string) => `${a} витягує карту в ${b}`,
    regen: (p: string) => `${p} самовідновлюється +1`,
    lash: (a: string, b: string) => `${a} гине з боєм — ${b} отримує 1`,
    bounty: (p: string) => `${p} забирає нагороду — тягне 3 карти`,
    penalty: (p: string) => `${p} усунув власну Безпеку — скидає всі карти`,
    flat: (p: string, r: string) => `${p} вибуває — це був ${r}`,
    leave: (p: string) => `${p} від'єднується від мережі`,
    virus: (p: string) => `вірус гризе ${p}`,
    virusJump: (p: string) => `вірус перескакує на ${p}`,
    virusOut: () => 'вірус згасає',
    dtSkip: (p: string) => `${p} застряг у простої — хід пропущено`,
    dtFree: (p: string) => `${p} скидає простій`,
    reshuffle: () => 'мережа перетасовує скид у нову колоду',
    toHit: (p: string) => `${p} завис — Спайк влучає (тайм-аут)`,
    toSkip: (p: string) => `${p} зволікає — хід пропущено (тайм-аут)`,
    win: (w: string) => `забіг завершено: ${w}`,
  },
  stats: {
    title: 'Статистика матчу',
    mvp: 'MVP',
    dealt: 'Шкода',
    taken: 'Отримано',
    spikes: 'Спайки',
    blocks: 'Блоки',
    heals: 'Лікування',
  },
  howto: {
    title: 'Як грати',
    intro:
      'NEONFALL — перестрілка з прихованими ролями на неоновій мережі. 4–8 ранерів, таємні фракції, один відкритий Директор. Читайте стіл, розумно витрачайте карти й станьте останньою фракцією в мережі.',
    factionsHeader: 'Фракції',
    charactersHeader: 'Персонажі',
    charactersNote: 'Кожен ранер отримує випадкового персонажа — ♥ це стартова цілісність (Директор отримує +1 зверху):',
    cardsHeader: 'Карти',
    cardsNote: 'Торкніться ранера у грі або відкрийте Кодекс карт — там усі карти за категоріями.',
    sections: [
      { h: 'Мета', b: '4–8 ранерів сидять у кільці, поділені на таємні фракції. Директор відомий усім; решта — загадка. Перемагає остання фракція, що лишилася живою.' },
      { h: 'Ваш хід', b: 'Три такти: ДОБІР двох карт, ХІД (лише один Спайк за хід, якщо спорядження не каже інакше), потім СКИД до рівня цілісності.' },
      { h: 'Мережева близькість', b: 'Радіус вирішує все. Спайкати можна лише ранерів у радіусі — базовий радіус 1 місце. Зброя збільшує його; щити тримають інших далі. Коли ранери вибувають, кільце стискається й далекі цілі стають досяжними.' },
      { h: 'Бій', b: 'Спайк завдає 1 шкоди. Ціль може відповісти Фаєрволом, щоб скасувати його, або Шифрощит може заблокувати з певним шансом. Немає відповіді — мінус цілісність. Дійшли до нуля — вибуваєте, роль розкривається.' },
      { h: 'Нагороди', b: 'Усуньте Бунтаря — і тягнете 3 карти як нагороду. Але Директор, який вибиває власну Охорону, зганьблений: він скидає всю руку та спорядження до відбою.' },
      { h: 'Перемога', b: 'Корпорація (Директор + Охорона) перемагає, зачистивши всіх Бунтарів і Фіксерів. Бунтарі перемагають, убивши Директора. Самотній Фіксер перемагає, лишившись останнім живим.' },
    ],
  },
  botLines: {
    dir_fixer: 'Фіксера першим.',
    dir_focus: 'Фокус вогонь.',
    dir_guilty: 'Ти підозрілий.',
    dir_guard: 'Прикрийте мене.',
    dir_liars: 'Двоє брешуть.',
    dir_rebels: 'Ребели близько.',
    bluff_loyal: 'Я свій.',
    bluff_withDir: 'Я за Корпу.',
    bluff_clean: 'Я чистий 😇',
    bluff_noSpike: 'Не пали Спайк.',
    bluff_quiet: 'Дивись на тихих.',
    bluff_notMe: 'Це не я.',
    bluff_wrong: 'Не та ціль.',
    bluff_gotBack: 'Я прикриваю Дира.',
    bluff_someone: 'Хтось бреше.',
    bluff_cover: 'Прикрий мене.',
  },
};

export const copy: Record<Lang, Copy> = { en, uk };

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const LANG_KEY = 'neonfall.lang';

function initialLang(): Lang {
  // The dedicated /uk/ entry exists specifically to serve Ukrainian content
  // (its own hreflang/canonical promise this to search engines), so it wins
  // over a remembered choice or browser locale.
  if (location.pathname.startsWith('/uk')) return 'uk';
  const saved = localStorage.getItem(LANG_KEY);
  if (saved === 'en' || saved === 'uk') return saved;
  return navigator.language?.toLowerCase().startsWith('uk') ? 'uk' : 'en';
}

interface LangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Copy;
}

const Ctx = createContext<LangCtx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem(LANG_KEY, l);
  };
  return <Ctx.Provider value={{ lang, setLang, t: copy[lang] }}>{children}</Ctx.Provider>;
}

export function useI18n(): LangCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n must be used within LangProvider');
  return ctx;
}

/** Shorthand for components that only need the copy. */
export function useT(): Copy {
  return useI18n().t;
}

/**
 * Render one turnLog line in the user's language. Lines are JSON log events;
 * anything unparseable (old games) is shown verbatim.
 */
export function renderLogLine(t: Copy, line: string, nameOf: (uid: string) => string): string {
  const ev = parseLogEvent(line);
  if (!ev) return line;
  const L = t.log;
  switch (ev.t) {
    case 'start': return L.start(nameOf(ev.d));
    case 'draw': return L.draw(nameOf(ev.p));
    case 'salvage': return L.salvage(nameOf(ev.p), t.card.name[ev.k]);
    case 'spike': return L.spike(nameOf(ev.a), nameOf(ev.b));
    case 'shield': return L.shield(nameOf(ev.p));
    case 'mayFw': return L.mayFw(nameOf(ev.p));
    case 'fw': return L.fw(nameOf(ev.p));
    case 'burst': return L.burst(nameOf(ev.p));
    case 'ride': return L.ride(nameOf(ev.p));
    case 'duel': return L.duel(nameOf(ev.a), nameOf(ev.b), nameOf(ev.l));
    case 'raid': return L.raid(nameOf(ev.a), nameOf(ev.b));
    case 'siphon': return L.siphon(nameOf(ev.a), nameOf(ev.b));
    case 'stim': return L.stim(nameOf(ev.p));
    case 'trace': return L.trace(nameOf(ev.a), nameOf(ev.b), t.role.name[ev.r]);
    case 'airGap': return L.airGap(nameOf(ev.p), t.card.name[ev.k]);
    case 'killSwitch': return L.killSwitch(nameOf(ev.a), nameOf(ev.b), t.card.name[ev.k]);
    case 'backchannel': return L.backchannel(nameOf(ev.a), nameOf(ev.b));
    case 'fieldPatch': return L.fieldPatch(nameOf(ev.a), nameOf(ev.b));
    case 'coldReboot': return L.coldReboot(nameOf(ev.p), ev.n);
    case 'packetStorm': return L.packetStorm(nameOf(ev.p));
    case 'emp': return L.emp(nameOf(ev.p));
    case 'med': return L.med(nameOf(ev.p));
    case 'pay': return L.pay(nameOf(ev.p));
    case 'equip': return L.equip(nameOf(ev.p), t.card.name[ev.k]);
    case 'plant': return L.plant(nameOf(ev.p), t.card.name[ev.k], nameOf(ev.b));
    case 'mustDiscard': return L.mustDiscard(nameOf(ev.p), ev.n);
    case 'discard': return L.discard(nameOf(ev.p), ev.n);
    case 'dmg': return L.dmg(nameOf(ev.p), ev.n);
    case 'surge': return L.surge(nameOf(ev.p), ev.n);
    case 'reboot': return L.reboot(nameOf(ev.p));
    case 'leech': return L.leech(nameOf(ev.a), nameOf(ev.b));
    case 'regen': return L.regen(nameOf(ev.p));
    case 'lash': return L.lash(nameOf(ev.a), nameOf(ev.b));
    case 'bounty': return L.bounty(nameOf(ev.p));
    case 'penalty': return L.penalty(nameOf(ev.p));
    case 'flat': return L.flat(nameOf(ev.p), t.role.name[ev.r]);
    case 'leave': return L.leave(nameOf(ev.p));
    case 'virus': return L.virus(nameOf(ev.p));
    case 'virusJump': return L.virusJump(nameOf(ev.p));
    case 'virusOut': return L.virusOut();
    case 'dtSkip': return L.dtSkip(nameOf(ev.p));
    case 'dtFree': return L.dtFree(nameOf(ev.p));
    case 'reshuffle': return L.reshuffle();
    case 'toHit': return L.toHit(nameOf(ev.p));
    case 'toSkip': return L.toSkip(nameOf(ev.p));
    case 'win': return L.win(t.winner[ev.w]);
    default: return line;
  }
}
